import { useEffect, useMemo, useRef, useState } from 'react';
import type { Property } from '../../../../domain/property-types';
import type {
  ElementIdentifierDbidType,
  ProjectSettingsType,
} from '../../../../gql/graphql';
import type {
  ModelsMap,
  DbIdsPerModel,
} from '../../../../components/common/ForgeViewer';
import type { PropertyContext } from '../../../../domain/property-operations';
import { resolveProperties } from '../../../../domain/property-operations';
import { convertToElementIdentifierDbidType } from '../../../../utils/dbid-utils';
import {
  HistoricalPropertyContext,
  useProjectHistory,
} from './useProjectHistory';
import { useDiffing } from './useDiffing';

export type HistoricalPropsResult = {
  isBimSelection: boolean;
  propertyDiffMap: Record<string, { old?: unknown; new?: unknown }>;
  referenceResolvedProperties: Property[] | null;
  newHistoricalPropertyContext: PropertyContext | null;
};

export type ViewerAdapter = {
  loadedModels: ModelsMap;
};

export function useHistoricalProperties(args: {
  resolveForDbIds: DbIdsPerModel;
  settings: ProjectSettingsType | undefined;
  propertyContext: PropertyContext;
  viewer: ViewerAdapter;
}): HistoricalPropsResult {
  const { resolveForDbIds, settings, propertyContext, viewer } = args;

  const { isHistoryDrawerOpen, referenceState, diff } = useProjectHistory();
  const { getHistoricalProperties, getHistoricalContexts } = useDiffing();

  const [propertyDiffMap, setPropertyDiffMap] = useState<
    Record<string, { old?: unknown; new?: unknown }>
  >({});
  const [referenceResolvedProperties, setReferenceResolvedProperties] =
    useState<Property[] | null>(null);
  const [newHistoricalPropertyContext, setNewHistoricalPropertyContext] =
    useState<PropertyContext | null>(null);

  const lastSignatureRef = useRef<string | null>(null);

  const cleanedProjectSettings = useMemo(
    () => ({
      maxTrianglesForComplexAlgorithm:
        settings?.polygonCalculationLimit || 5000,
      globalOffset: [
        settings?.viewerGlobalOffset?.x || 0,
        settings?.viewerGlobalOffset?.y || 0,
        settings?.viewerGlobalOffset?.z || 0,
      ] as [number, number, number],
    }),
    [settings]
  );

  const diffingSettings = useMemo(
    () => ({
      polygonCalculationLimit: settings?.polygonCalculationLimit ?? undefined,
      viewerGlobalOffset: settings?.viewerGlobalOffset
        ? {
            x: settings.viewerGlobalOffset.x ?? undefined,
            y: settings.viewerGlobalOffset.y ?? undefined,
            z: settings.viewerGlobalOffset.z ?? undefined,
          }
        : undefined,
    }),
    [settings?.polygonCalculationLimit, settings?.viewerGlobalOffset]
  );

  const isBimSelection = useMemo(() => {
    try {
      const shapes = (propertyContext as any)?.loadedShapes || {};
      const sheetShapes = (propertyContext as any)?.loadedSheetShapes || {};
      for (const [modelUrn, ids] of Object.entries(resolveForDbIds)) {
        const arr3d = Array.isArray(
          (shapes as Record<string, unknown[]>)[modelUrn]
        )
          ? ((shapes as Record<string, unknown[]>)[modelUrn] as Array<{
              dbId: number;
            }>)
          : [];
        const arr2d = Array.isArray(
          (sheetShapes as Record<string, unknown[]>)[modelUrn]
        )
          ? ((sheetShapes as Record<string, unknown[]>)[modelUrn] as Array<{
              dbId: number;
            }>)
          : [];
        const idList = Array.isArray(ids) ? (ids as number[]) : [];
        const hits3d = arr3d.some((s) => idList.includes(s.dbId));
        if (hits3d) return false;
        const hits2d = arr2d.some((s) => idList.includes(s.dbId));
        if (hits2d) return false;
      }
      return true;
    } catch {
      return false;
    }
  }, [propertyContext, resolveForDbIds]);

  const diffLogSignature = useMemo(() => {
    if (!diff) return 'no-diff';
    return JSON.stringify({
      diffKeys: Object.keys(diff).sort(),
      sel: resolveForDbIds,
    });
  }, [diff, resolveForDbIds]);

  // Compute property diffs (Shapes/SheetShapes only)
  useEffect(() => {
    if (!isHistoryDrawerOpen || !referenceState || !diff) return;
    if (isBimSelection) {
      setPropertyDiffMap({});
      return;
    }
    (async () => {
      const result = await getHistoricalProperties(
        referenceState,
        diff,
        resolveForDbIds,
        diffingSettings
      );
      if (Object.keys(result).length > 0) {
        if (lastSignatureRef.current !== diffLogSignature) {
          lastSignatureRef.current = diffLogSignature;
        }
        setPropertyDiffMap(result);
      } else {
        setPropertyDiffMap({});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isHistoryDrawerOpen,
    referenceState,
    diff,
    resolveForDbIds,
    diffingSettings,
    diffLogSignature,
    isBimSelection,
  ]);

  // Compute reference-side properties and new historical context (Shapes/SheetShapes only)
  useEffect(() => {
    const selectedCount = Object.values(resolveForDbIds).flat().length;
    if (
      !isHistoryDrawerOpen ||
      !referenceState ||
      !diff ||
      selectedCount !== 1
    ) {
      setReferenceResolvedProperties(null);
      setNewHistoricalPropertyContext(null);
      return;
    }
    (async () => {
      if (isBimSelection) {
        setReferenceResolvedProperties(null);
        setNewHistoricalPropertyContext(null);
        return;
      }

      const { oldCtx, newCtx } = getHistoricalContexts(referenceState, diff);
      const rawCtx =
        oldCtx ?? (referenceState.propertyContext as HistoricalPropertyContext);

      const urnsFromState = rawCtx.loadedModels;
      const stateHydratedModels: ModelsMap = {};
      urnsFromState.forEach((urn) => {
        const modelInstance = viewer.loadedModels[urn];
        if (modelInstance) {
          stateHydratedModels[urn] = modelInstance;
        }
      });

      let ctx = {
        ...rawCtx,
        loadedModels: stateHydratedModels,
      } as PropertyContext;

      const dbIdsList: ElementIdentifierDbidType[] =
        convertToElementIdentifierDbidType(resolveForDbIds);

      // Ensure selected URNs are overlay-loaded
      const selectedUrns = Object.keys(resolveForDbIds);

      // Hydrate reference ctx with live viewer models
      const mergedModels: ModelsMap = {
        ...(ctx.loadedModels ?? ({} as ModelsMap)),
      };
      selectedUrns.forEach((urn) => {
        const m = viewer.loadedModels[urn];
        if (m) mergedModels[urn] = m;
      });
      ctx = { ...ctx, loadedModels: mergedModels };

      // Hydrate NEW context for later usage (visualization, algorithms)
      let hydratedNewCtx: PropertyContext | null = null;
      if (newCtx) {
        const mergedNew: ModelsMap = {};
        newCtx.loadedModels.forEach((urn) => {
          const modelInstance = viewer.loadedModels[urn];
          if (modelInstance) {
            mergedNew[urn] = modelInstance;
          }
        });
        selectedUrns.forEach((urn) => {
          const m = viewer.loadedModels[urn];
          if (m) mergedNew[urn] = m;
        });
        hydratedNewCtx = { ...newCtx, loadedModels: mergedNew };
      }
      setNewHistoricalPropertyContext(hydratedNewCtx);

      const refProps = await resolveProperties(
        ctx,
        cleanedProjectSettings,
        undefined,
        dbIdsList
      );
      // Only use reference props if they contain visible BIM categories
      const hasVisibleBim = refProps.some(
        (p) =>
          Boolean(p?.attribute?.category) &&
          ![
            'Item',
            'Element ID',
            'Revit Type',
            'Shape',
            'Sparkel Attributes',
          ].includes(p.attribute.category) &&
          !p.attribute.category.includes('__')
      );
      setReferenceResolvedProperties(hasVisibleBim ? refProps : null);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isHistoryDrawerOpen,
    referenceState,
    diff,
    resolveForDbIds,
    cleanedProjectSettings,
    isBimSelection,
    viewer.loadedModels,
  ]);

  return {
    isBimSelection,
    propertyDiffMap,
    referenceResolvedProperties,
    newHistoricalPropertyContext,
  };
}
